/**
 * FindAbhaByMobile.tsx
 * Search ABHA accounts by mobile number, select one, verify via OTP, then navigate to success.
 *
 * Flow:
 *   1. Enter 10-digit mobile → search linked ABHA IDs
 *   2. Select an ABHA from the list
 *   3. Request OTP for the selected ABHA (index-based)
 *   4. Verify OTP → get profile
 *   5. Navigate to success
 */

import React, { useState } from 'react';
import { useAbdm } from '../context/AbdmContext';
import {
    handleSearchAbhaByMobile,
    handleSearchAbhaRequestOtp,
    handleVerifyOTP,
    handleViewProfile,
    AbhaProfileItem,
} from '../../utils/api';

type Phase = 'search' | 'selectAbha' | 'otpVerify';

export const FindAbhaByMobile: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { authData, authConfig } = state;

    const [phase, setPhase] = useState<Phase>('search');

    // Search phase
    const [mobile, setMobile] = useState('');
    const [searchTxnId, setSearchTxnId] = useState('');
    const [accounts, setAccounts] = useState<AbhaProfileItem[]>([]);

    // OTP phase
    const [selectedAccount, setSelectedAccount] = useState<AbhaProfileItem | null>(null);
    const [otpTxnId, setOtpTxnId] = useState('');
    const [maskedMobile, setMaskedMobile] = useState('');
    const [otpInput, setOtpInput] = useState('');

    // Shared
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // ── Step 1: Search by mobile ─────────────────────────────────────────
    const handleSearch = async () => {
        if (!authData || !/^\d{10}$/.test(mobile)) return;
        setLoading(true);
        setError('');
        try {
            const result = await handleSearchAbhaByMobile(authData, mobile);
            setSearchTxnId(result.txnId);
            if (result.abhaList && result.abhaList.length > 0) {
                setAccounts(result.abhaList);
                setPhase('selectAbha');
            } else {
                setError('No ABHA accounts found for this mobile number.');
            }
        } catch (err) {
            const msg = (err as Error).message;
            if (msg === 'USER_NOT_FOUND') {
                setError('No ABHA account found linked to this mobile number.');
            } else {
                setError(msg || 'Search failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Select ABHA → request OTP ────────────────────────────────
    const handleSelectAbha = async (account: AbhaProfileItem) => {
        if (!authData) return;
        setSelectedAccount(account);
        setLoading(true);
        setError('');
        try {
            const result = await handleSearchAbhaRequestOtp(authData, searchTxnId, account.index);
            setOtpTxnId(result.txnId);
            if (result.maskedMobile) setMaskedMobile(result.maskedMobile);
            setPhase('otpVerify');
        } catch (err) {
            setError((err as Error).message || 'Failed to request OTP for selected ABHA.');
        } finally {
            setLoading(false);
        }
    };

    // ── Step 3: Verify OTP → get profile ─────────────────────────────────
    const handleOtpVerify = async () => {
        if (!authData || !selectedAccount) return;
        setLoading(true);
        setError('');
        try {
            // Backend expects type='abha-number' for search-abha verify (same as HealthProPluse)
            const verifyConfig = {
                ...authConfig,
                type: 'abha-number' as const,
                method: 'mobile' as const,
                value: selectedAccount.ABHANumber?.replace(/-/g, '') || '',
            };
            const result = await handleVerifyOTP(authData, verifyConfig, otpTxnId, otpInput);

            const xToken = result.xToken || '';
            if (xToken) {
                dispatch({ type: 'SET_X_TOKEN', payload: xToken });
            }
            if (result.txnId) {
                dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            }

            // Try to get full profile
            let profile = result.profile || null;
            if (xToken && !profile) {
                try {
                    profile = await handleViewProfile(authData, xToken, verifyConfig);
                } catch (e) {
                    console.warn('[FindAbhaByMobile] handleViewProfile failed:', e);
                }
            }

            if (profile) {
                dispatch({ type: 'SET_USER_PROFILE', payload: profile });
                dispatch({ type: 'SET_STEP', payload: 'success' });
            } else {
                setError('OTP verified but could not retrieve profile. Please try again.');
            }
        } catch (err) {
            const msg = (err as Error).message || '';
            if (msg.toLowerCase().includes('otp') || msg.toLowerCase().includes('invalid')) {
                setError('Invalid OTP. Please check and try again.');
            } else {
                setError(msg || 'OTP verification failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Navigation ───────────────────────────────────────────────────────
    const goBack = () => {
        if (phase === 'otpVerify') { setOtpInput(''); setPhase('selectAbha'); return; }
        if (phase === 'selectAbha') { setPhase('search'); return; }
        dispatch({ type: 'SET_STEP', payload: 'authType' });
    };

    if (!authData) return null;

    // ── Search Phase ─────────────────────────────────────────────────────
    if (phase === 'search') {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>🔍</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Find ABHA by Mobile</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Search all ABHA IDs linked to a mobile number, then verify via OTP
                        </p>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="findMobile">
                        📱 Patient's Mobile Number <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="findMobile"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="Enter 10-digit mobile number"
                        value={mobile}
                        onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onKeyDown={e => e.key === 'Enter' && mobile.length === 10 && handleSearch()}
                        autoFocus
                    />
                    {mobile.length > 0 && mobile.length < 10 && (
                        <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>
                            Must be exactly 10 digits ({mobile.length}/10)
                        </p>
                    )}
                    {mobile.length === 10 && (
                        <p style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 4 }}>
                            ✓ Valid mobile number
                        </p>
                    )}
                </div>

                <button
                    className="btn-primary"
                    disabled={mobile.length !== 10 || loading}
                    onClick={handleSearch}
                    style={{ marginTop: '0.5rem' }}
                >
                    {loading ? '⏳ Searching...' : '🔍 Search ABHA'}
                </button>

                <button
                    className="btn-secondary"
                    onClick={() => dispatch({ type: 'SET_STEP', payload: 'authType' })}
                    style={{ marginTop: '0.5rem' }}
                >
                    ⬅ Back
                </button>
            </div>
        );
    }

    // ── Select ABHA Phase ────────────────────────────────────────────────
    if (phase === 'selectAbha') {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>👤</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Select ABHA Account</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Choose the account to verify via Mobile OTP
                        </p>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                    {accounts.length} account{accounts.length !== 1 ? 's' : ''} found for mobile {mobile}
                </div>

                <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {accounts.map((acct, idx) => {
                        const isKyc = acct.kycVerified === 'true';
                        const initial = (acct.name || 'A').charAt(0).toUpperCase();
                        return (
                            <div
                                key={acct.ABHANumber || idx}
                                className="flow-option"
                                onClick={() => !loading && handleSelectAbha(acct)}
                                style={{ cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem' }}
                            >
                                <div style={{
                                    width: 40, height: 40, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
                                }}>
                                    {initial}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{acct.name}</div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>
                                        {acct.ABHANumber || 'N/A'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 3,
                                            fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                                            background: isKyc ? '#ecfdf5' : '#fffbeb',
                                            color: isKyc ? '#059669' : '#d97706',
                                            border: `1px solid ${isKyc ? '#a7f3d0' : '#fde68a'}`,
                                            textTransform: 'uppercase',
                                        }}>
                                            {isKyc ? '🛡 Verified' : '⚠ Non-KYC'}
                                        </span>
                                        {acct.gender && (
                                            <span style={{
                                                fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                                                borderRadius: 6, background: '#f1f5f9', color: '#64748b',
                                                border: '1px solid #e2e8f0',
                                            }}>
                                                {acct.gender}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📱</span>
                            </div>
                        );
                    })}
                </div>

                {loading && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', color: '#3b82f6', fontSize: '0.85rem', fontWeight: 600 }}>
                        ⏳ Requesting OTP...
                    </div>
                )}

                <button
                    className="btn-secondary"
                    onClick={goBack}
                    style={{ marginTop: '0.75rem' }}
                >
                    ⬅ Back to Search
                </button>
            </div>
        );
    }

    // ── OTP Verify Phase ─────────────────────────────────────────────────
    if (phase === 'otpVerify') {
        return (
            <div className="step-container">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>🔐</span>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Verify OTP</h2>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Verifying: <strong>{selectedAccount?.name}</strong> ({selectedAccount?.ABHANumber})
                        </p>
                    </div>
                </div>

                {maskedMobile && (
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '0.9em' }}>
                        OTP sent to: <strong>{maskedMobile}</strong>
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="searchOtp">Enter 6-digit OTP</label>
                    <input
                        id="searchOtp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter OTP"
                        value={otpInput}
                        onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={e => e.key === 'Enter' && otpInput.length === 6 && handleOtpVerify()}
                        autoFocus
                    />
                </div>

                <div className="action-buttons">
                    <button
                        className="btn-secondary"
                        onClick={goBack}
                    >
                        ⬅ Back
                    </button>
                    <button
                        className="btn-primary"
                        disabled={otpInput.length !== 6 || loading}
                        onClick={handleOtpVerify}
                    >
                        {loading ? '🔍 Verifying...' : '✅ Verify OTP'}
                    </button>
                </div>
            </div>
        );
    }

    return null;
};
